/**
 * Card de KPI com breakdown de status (semáforo)
 */
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface KpiBreakdown {
  regular: number;
  emAndamento: number;
  irregular: number;
}

interface StatusDetail {
  vigente: number;
  permanente: number;
  isento: number;
  vigenteEmRenovacao: number;
}

interface KpiCardProps {
  title: string;
  total: number;
  subtitle?: string;
  breakdown?: KpiBreakdown;
  statusDetail?: StatusDetail;
  icon?: React.ReactNode;
  isLoading?: boolean;
  className?: string;
  onClick?: () => void;
  onBreakdownClick?: (category: 'regular' | 'emAndamento' | 'irregular') => void;
}

export function KpiCard({
  title,
  total,
  subtitle,
  breakdown,
  statusDetail,
  icon,
  isLoading = false,
  className,
  onClick,
  onBreakdownClick,
}: KpiCardProps) {
  // Calcula percentuais para mostrar no tooltip
  const getPercent = (value: number) => {
    if (!breakdown) return 0;
    const sum = breakdown.regular + breakdown.emAndamento + breakdown.irregular;
    return sum > 0 ? ((value / sum) * 100).toFixed(1) : '0';
  };

  if (isLoading) {
    return (
      <div className={cn(
        'bg-card rounded-lg border p-4 animate-pulse',
        className
      )}>
        <div className="h-4 bg-muted rounded w-24 mb-2" />
        <div className="h-8 bg-muted rounded w-16 mb-3" />
        <div className="flex gap-2">
          <div className="h-6 bg-muted rounded flex-1" />
          <div className="h-6 bg-muted rounded flex-1" />
          <div className="h-6 bg-muted rounded flex-1" />
        </div>
      </div>
    );
  }

  return (
    <div 
      className={cn(
        'bg-card rounded-lg border p-4 transition-all hover:shadow-md flex flex-col',
        onClick && 'cursor-pointer hover:border-primary/50',
        className
      )}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-muted-foreground">
          {title}
        </span>
        {icon && (
          <span className="text-muted-foreground">
            {icon}
          </span>
        )}
      </div>

      {/* Total */}
      <div className="text-3xl font-bold">
        {total.toLocaleString('pt-BR')}
        {subtitle && (
          <span className="text-sm font-normal text-muted-foreground ml-2">
            {subtitle}
          </span>
        )}
      </div>

      {/* Status Detail Legend (para cards de regulares) */}
      {statusDetail && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
          {statusDetail.vigente > 0 && (
            <span>Vigente: <strong className="text-foreground">{statusDetail.vigente.toLocaleString('pt-BR')}</strong></span>
          )}
          {statusDetail.permanente > 0 && (
            <span>Permanente: <strong className="text-foreground">{statusDetail.permanente.toLocaleString('pt-BR')}</strong></span>
          )}
          {statusDetail.isento > 0 && (
            <span>Isento: <strong className="text-foreground">{statusDetail.isento.toLocaleString('pt-BR')}</strong></span>
          )}
          {statusDetail.vigenteEmRenovacao > 0 && (
            <span>Vigente/Renovação: <strong className="text-foreground">{statusDetail.vigenteEmRenovacao.toLocaleString('pt-BR')}</strong></span>
          )}
        </div>
      )}

      {/* Spacer for flex alignment */}
      {!breakdown && !statusDetail && <div className="flex-1" />}

      {/* Breakdown (semáforo) */}
      {breakdown && (
        <div className="flex gap-2 mt-3">
          {/* Regular (verde) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onBreakdownClick?.('regular');
            }}
            className={cn(
              'flex-1 bg-emerald-500/10 rounded px-2 py-1 text-center transition-all',
              onBreakdownClick && 'hover:bg-emerald-500/20 cursor-pointer hover:scale-[1.02]'
            )}
            title={`${getPercent(breakdown.regular)}% do total`}
          >
            <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
              {breakdown.regular.toLocaleString('pt-BR')}
            </div>
            <div className="text-[10px] text-emerald-600/70 dark:text-emerald-400/70">
              Regular
            </div>
          </button>

          {/* Em Andamento (amarelo) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onBreakdownClick?.('emAndamento');
            }}
            className={cn(
              'flex-1 bg-amber-500/10 rounded px-2 py-1 text-center transition-all',
              onBreakdownClick && 'hover:bg-amber-500/20 cursor-pointer hover:scale-[1.02]'
            )}
            title={`${getPercent(breakdown.emAndamento)}% do total`}
          >
            <div className="text-sm font-semibold text-amber-600 dark:text-amber-400">
              {breakdown.emAndamento.toLocaleString('pt-BR')}
            </div>
            <div className="text-[10px] text-amber-600/70 dark:text-amber-400/70">
              Andamento
            </div>
          </button>

          {/* Irregular (vermelho) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onBreakdownClick?.('irregular');
            }}
            className={cn(
              'flex-1 bg-red-500/10 rounded px-2 py-1 text-center transition-all',
              onBreakdownClick && 'hover:bg-red-500/20 cursor-pointer hover:scale-[1.02]'
            )}
            title={`${getPercent(breakdown.irregular)}% do total`}
          >
            <div className="text-sm font-semibold text-red-600 dark:text-red-400">
              {breakdown.irregular.toLocaleString('pt-BR')}
            </div>
            <div className="text-[10px] text-red-600/70 dark:text-red-400/70">
              Irregular
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
